// Sugalarm — thin top-of-screen banner for `high` episodes (cycle 36.1).
//
// Symmetric companion to LowAlarmBanner.tsx. Cycle 33 demoted regular
// `low` to a thin banner above the Now grid; regular `high` had no
// alarm chrome through cycle 36. Cycle 36.1 brings symmetry — a real
// `high` reading (target_high < sgv <= urgent_high) now opens an
// episode and renders this banner, and the cycle-36 preview surface
// fires it on the High preview button too.
//
// Color treatment: shares `var(--zone-warn)` with LowAlarmBanner —
// no `var(--zone-warn-strong)` token exists yet and the cycle spec
// rules out inventing one here. A future design pass can add a
// strong-variant token + flip this banner to it; the structural
// shape stays identical so the swap is one line.
//
// ⚠ CYCLE 454 (operator decisions): THIS BANNER CARRIES BOTH HIGH LEVELS,
// AND IT IS STOP-ONLY.
//   - Urgent High no longer takes over the screen; it renders HERE, in the
//     urgent red, with its own label. Urgent Low's takeover is untouched.
//   - No Snooze on any state. Stop is the right-aligned `AlarmStopPill`
//     (compact pill, 44 × 44 px target), shared with the off-Now banner.
//   - Bedside clock mode no longer covers the face with `ExpandedClockAlarm`:
//     every state is the fixed bottom strip, the same banner as elsewhere.
//   - The label shows only the level and the stop state ("· Stopped", or
//     the "· NOT STOPPED" failed-ack suffix) — never a snooze countdown.
// States:
//   active, or a snooze already running (a native client's, or one taken
//   before 454 — it expires normally) → level + Stop pill
//   stopped                           → "<level> · Stopped", no control (no
//                                        audio can sound; a re-arm clears it)
//   failed ack                        → forced active + the suffix

import { displayValue } from "../../lib/glucose";
import type { AlarmEpisodeState } from "../../hooks/useAlarmEpisode";
import type { FrameVariant } from "../../hooks/useFrameVariant";
// Cycle 166: presentation-only clock-mode read + the shared expanded
// form — see LowAlarmBanner for the full gate rationale.
import { useClockMode } from "../../hooks/useAlarmClockMode";
import {
  ALARM_BANNER_PAD_Y,
  ALARM_BANNER_ROW_PX,
  alarmBannerDot,
  alarmBannerGround,
  alarmTone,
} from "./alarmBannerStyle";
import { AlarmStopPill } from "./AlarmStopPill";

interface Props {
  episode: AlarmEpisodeState;
  units: "mg/dL" | "mmol/L";
  // Cycle 303: mobile-compact strip drops the reading + ellipsizes.
  // See LowAlarmBanner for the full rationale. Kiosk/web byte-identical.
  variant?: FrameVariant;
}

export function HighAlarmBanner({ episode, units, variant }: Props) {
  // Cycle 303: mobile-compact presentation (drop reading, ellipsize).
  const mobile = variant === "mobile";
  // Hook before the early returns (Rules of Hooks).
  const clockModeActive = useClockMode().active;
  // Cycle 81: same two-gate fix as LowAlarmBanner — gate on the
  // phase-live predicate + the persisted episode level (not the live
  // reading zone), and make the state machine exhaustive with a
  // default control branch so the `high` recovery-dwell window (and
  // the post-snooze re-armed state) can never leave an empty bar
  // mounted while the loop can still sound.
  const level = episode.episodeLevel;
  if (!episode.isLive || (level !== "high" && level !== "urgent_high")) {
    return null;
  }
  const levelLabel = level === "urgent_high" ? "Urgent High" : "High";
  const tone = alarmTone(level);
  // Cycle 304, WIDENED IN CYCLE 398: a high back in range on a fresh,
  // strictly-newer reading hides on that FIRST reading, in ANY phase (the
  // cycle-304 "muted only" restriction is gone) — display only; the server
  // still owns recovery. "It does not matter if it's coming from a high or
  // a low" is the operator rule, so this is byte-for-byte the low-side
  // treatment. See LowAlarmBanner for the full rationale.
  if (episode.bannerSuppressedInRange) return null;
  // ⚠ CYCLE 416: A FAILED ACK OUTRANKS THE SNOOZED / STOPPED PRESENTATION.
  // If the ack never landed, the alarm is still live — so the banner must
  // NOT render the calm state the user was expecting. Forcing `showActive`
  // does three things at once: it stops the UI claiming a snooze the server
  // never granted, it puts the controls back on screen (the standing rule
  // that at least one alarm control is present whenever audio can sound),
  // and those controls ARE the retry affordance.
  const ackFailed = episode.ackFailure != null;
  const showSnoozing = !ackFailed && episode.phase === "snoozed";
  const showStopped = !ackFailed && !showSnoozing && episode.stopped;
  const showActive = !showSnoozing && !showStopped;
  const sgvText =
    episode.sgv != null ? displayValue(episode.sgv, units) : "—";

  // Cycle 166's clock-mode takeover is gone (cycle 454): in clock mode
  // every state is the fixed bottom strip below. Stop is offered whenever
  // the banner is not in its Stopped state — active, a running snooze, or a
  // failed ack (which forces `showActive`).

  return (
    <div
      role="status"
      aria-live="polite"
      // Cycle 85 / 103: the `.kiosk`-scoped rule keeps this strip above
      // any kiosk chrome (it originally lifted it over the cycle-79 curved
      // rail seam, removed in cycle 103; the rule stays as a defensive
      // floor). No-op on web/mobile (no `.kiosk` ancestor). See tokens.css
      // `.alarmBannerStrip`.
      className="alarmBannerStrip"
      // Cycle 452.2: an inert marker the type-floor sweep finds alarm
      // controls by (44 px buttons, 4.5:1 text).
      data-alarm-control="banner"
      style={{
        flexShrink: 0,
        // Cycle 452.1 (phones): the row may GROW when the status suffix has
        // to wrap ("· NOT SNOOZED" is alarm state and is never cut off).
        // Kiosk and web keep the fixed single row.
        // Cycle 452.2: the row carries 44 px buttons now (it held 32 px ones
        // in a 40 px row) — a single line is 52 px on every surface.
        ...(mobile
          ? { minHeight: ALARM_BANNER_ROW_PX, padding: `${ALARM_BANNER_PAD_Y}px 16px` }
          : { height: ALARM_BANNER_ROW_PX, padding: "0 16px" }),
        // Cycle 452.2: an OPAQUE amber with deep-shade ink (about 9:1). The
        // cycle-91.1 translucent kiosk fill and its white copy read at under
        // 2:1 on amber, and at no bound at all over a photo.
        // Cycle 454: amber for High, the urgent red for Urgent High — both
        // the shared opaque grounds (4.5:1, alarmBanner.test.ts).
        ...(tone === "urgent"
          ? { ...alarmBannerGround("urgent") }
          : { ...alarmBannerGround("warn") }),
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontFamily: "inherit",
        fontWeight: 700,
        // Cycle 302: smaller banner text + a single-line guarantee so the
        // longer urgent labels ("URGENT HIGH · 250 mg/dL") never wrap to a
        // second line inside this fixed ~40 px row. `white-space` is
        // inherited, so setting it on the flex container pins every text
        // child (label, sgv, subtext) to one line; the flex items no
        // longer shrink-and-wrap.
        fontSize: 13,
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
        boxShadow: "0 1px 0 rgba(0, 0, 0, 0.18)",
        // Cycle 452.2: the Stopped strip is no longer dimmed with `opacity` —
        // that lowered its text's contrast. The still dot and "High · Stopped"
        // carry the muted state.
        // Cycle 167: clock-mode collapsed strip pins full-width to the
        // bottom edge — same gate + rationale as LowAlarmBanner; the
        // face reserves the band. Outside clock mode: byte-identical.
        ...(clockModeActive
          ? {
              position: "fixed" as const,
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 50,
              boxShadow: "0 -1px 0 rgba(0, 0, 0, 0.18)",
            }
          : null),
      }}
    >
      <span
        aria-hidden
        style={alarmBannerDot(!showStopped)}
      />
      <span
        style={{
          textTransform: "uppercase",
          letterSpacing: mobile ? "0.06em" : "0.08em",
          // Cycle 452.1 (phones): the label WRAPS instead of ellipsizing —
          // each status fragment stays whole (its own nowrap span), so a
          // break can only fall between "Low" and "· NOT SNOOZED". The
          // action cluster still never shrinks.
          ...(mobile
            ? {
                flex: "1 1 auto",
                minWidth: 0,
                whiteSpace: "normal",
                lineHeight: 1.25,
              }
            : null),
        }}
      >
        <span style={{ whiteSpace: "nowrap" }}>
          {showStopped ? `${levelLabel} · Stopped` : levelLabel}
        </span>
        {/* Cycle 416: name the failure inline. The banner is a fixed
            single-line row, so this is a suffix rather than a second
            row — the full sentence lives on the roomier surfaces. It
            never claims the alarm is handled. */}
        {ackFailed ? (
          <>
            {" "}
            <span style={{ whiteSpace: "nowrap" }} data-testid="ack-suffix">
              {episode.ackFailure?.action === "snooze" ? "· NOT SNOOZED" : "· NOT STOPPED"}
            </span>
          </>
        ) : null}
      </span>
      {/* Cycle 303: reading dropped on the mobile strip; kiosk/web keep it. */}
      {!mobile && (
        <>
          <span style={{ opacity: 0.7 }} aria-hidden>
            ·
          </span>
          <span className="tnum">
            {sgvText} {units}
          </span>
          <span style={{ flex: 1 }} />
        </>
      )}
      {showActive && (
        <AlarmStopPill tone={tone} onStop={episode.stop} testId="high-alarm-stop" />
      )}
      {showSnoozing && (
        // A snooze already on the row (a native client's, or one taken
        // before 454) runs out on its own; Stop still ends the episode.
        <AlarmStopPill tone={tone} onStop={episode.stop} testId="high-alarm-stop" />
      )}
    </div>
  );
}

